import QRCode from 'qrcode';
import JSZip from 'jszip';
import { buildFrontHtml, buildBackHtml } from './idCardHtmlBuilder';
import { ID_CARD_PORTRAIT, ID_CARD_LANDSCAPE } from './idCardTheme';
import { buildIdCardData } from './idCardCardData';
import { getTokenSync } from './authStorage';
import { downloadBlob } from './blobDownload';
import { getProfileAssetUrl } from './api';
import { mergePrintSettings } from './idCardPrintSettings';
import { buildPrintSheetHtml, computeSheetGrid } from './idCardSheetLayout';
import { buildSingleCardDocumentHtml, mmToCssPx } from './idCardDocument';

const CARD_FORMATS = {
  portrait: ID_CARD_PORTRAIT,
  landscape: ID_CARD_LANDSCAPE,
};

const IMAGE_URL_KEYS = ['logoUrl', 'photoUrl', 'principalSignatureUrl', 'footerBgUrl', 'managedLogoUrl'];

let html2pdfPromise = null;
function getHtml2pdf() {
  if (!html2pdfPromise) html2pdfPromise = import('html2pdf.js').then((m) => m.default);
  return html2pdfPromise;
}

async function qrDataUrlFor(payload) {
  if (!payload) return '';
  try {
    return await QRCode.toDataURL(payload, { width: 480, margin: 1, errorCorrectionLevel: 'M' });
  } catch {
    return '';
  }
}

async function fetchAsDataUrl(url) {
  if (!url || String(url).startsWith('data:') || String(url).startsWith('blob:')) {
    return url || '';
  }
  try {
    const headers = {};
    const token = getTokenSync();
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url, { headers, credentials: 'same-origin', mode: 'cors' });
    if (!res.ok) return url;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result || url);
      reader.onerror = () => resolve(url);
      reader.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
}

async function inlineImageUrls(data) {
  const next = { ...data };
  await Promise.all(
    IMAGE_URL_KEYS.map(async (key) => {
      if (next[key]) next[key] = await fetchAsDataUrl(next[key]);
    }),
  );
  return next;
}

function waitForImages(root, timeoutMs = 8000) {
  const imgs = root?.querySelectorAll?.('img') || [];
  if (!imgs.length) return Promise.resolve();
  const promises = Array.from(imgs).map(
    (img) => new Promise((resolve) => {
      if (img.complete) {
        resolve();
        return;
      }
      img.onload = () => resolve();
      img.onerror = () => resolve();
    }),
  );
  return Promise.race([
    Promise.all(promises),
    new Promise((r) => setTimeout(r, timeoutMs)),
  ]);
}

function sizeCaptureIframe(iframe, widthPx, heightPx) {
  iframe.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${widthPx}px`,
    `height:${heightPx}px`,
    'border:0',
    'opacity:1',
    'pointer-events:none',
    'overflow:hidden',
  ].join(';');
}

function buildCaptureDocument(documentHtml, captureWidthPx, captureHeightPx) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  sizeCaptureIframe(iframe, captureWidthPx, captureHeightPx);
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) {
    iframe.remove();
    throw new Error('Could not create PDF capture frame');
  }

  doc.open();
  doc.write(documentHtml);
  doc.close();

  return { iframe, doc };
}

function fitIframeToContent(iframe, doc, fallbackWidthPx, fallbackHeightPx) {
  const w = Math.max(
    fallbackWidthPx,
    doc.documentElement?.scrollWidth || 0,
    doc.body?.scrollWidth || 0,
  );
  const h = Math.max(
    fallbackHeightPx,
    doc.documentElement?.scrollHeight || 0,
    doc.body?.scrollHeight || 0,
  );
  sizeCaptureIframe(iframe, Math.ceil(w), Math.ceil(h));
}

/** html2pdf only accepts a single HTMLElement — not NodeList. */
function getPdfCaptureElement(doc) {
  return doc.querySelector('.id-card-document') || doc.body;
}

function isCanvasTaintError(err) {
  const msg = String(err?.message || err || '').toLowerCase();
  return msg.includes('tainted') || msg.includes('tocanvas') || msg.includes('securityerror');
}

function html2pdfOptions(wMm, hMm, filename, captureWidthPx, captureHeightPx, scale = 2, allowTaint = false) {
  return {
    filename,
    margin: 0,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale,
      useCORS: true,
      allowTaint,
      logging: false,
      scrollX: 0,
      scrollY: 0,
      width: captureWidthPx,
      height: captureHeightPx,
    },
    jsPDF: { unit: 'mm', format: [wMm, hMm], orientation: wMm > hMm ? 'landscape' : 'portrait' },
    pagebreak: { mode: ['css', 'legacy'], avoid: ['img'] },
  };
}

async function captureElementToPdfBlob(html2pdf, captureEl, options) {
  if (!captureEl) {
    throw new Error('PDF capture element not found');
  }

  const run = async (allowTaint) => {
    const opts = html2pdfOptions(
      options.wMm,
      options.hMm,
      options.filename,
      options.captureWidthPx,
      options.captureHeightPx,
      options.scale,
      allowTaint,
    );
    const worker = html2pdf().set(opts).from(captureEl);
    const blob = await worker.output('blob');
    if (!blob || blob.size < 100) {
      throw new Error('Generated PDF was empty');
    }
    return blob;
  };

  try {
    return await run(false);
  } catch (err) {
    if (isCanvasTaintError(err)) {
      try {
        return await run(true);
      } catch (retryErr) {
        throw new Error(retryErr?.message || String(retryErr) || 'PDF generation failed');
      }
    }
    throw new Error(err?.message || String(err) || 'PDF generation failed');
  }
}

async function captureHtmlToPdfBlob(html2pdf, doc, options) {
  const captureEl = getPdfCaptureElement(doc);
  return captureElementToPdfBlob(html2pdf, captureEl, options);
}

/** Render each .id-card-page as its own PDF page (front + back for single-student download). */
async function capturePageElementsToPdfBlob(html2pdf, doc, options) {
  const pages = Array.from(doc.querySelectorAll('.id-card-page'));
  if (pages.length <= 1) {
    const el = pages[0] || getPdfCaptureElement(doc);
    return captureElementToPdfBlob(html2pdf, el, options);
  }

  const { wMm, hMm } = options;
  const orientation = wMm > hMm ? 'landscape' : 'portrait';

  const run = async (allowTaint) => {
    const opts = html2pdfOptions(
      wMm,
      hMm,
      options.filename,
      options.captureWidthPx,
      options.captureHeightPx,
      options.scale,
      allowTaint,
    );

    let pdf = null;
    for (let i = 0; i < pages.length; i++) {
      if (i === 0) {
        pdf = await html2pdf().set(opts).from(pages[i]).toContainer().toCanvas().toPdf().get('pdf');
        continue;
      }
      const worker = html2pdf().set(opts).from(pages[i]).toContainer().toCanvas();
      const canvas = await worker.get('canvas');
      if (!canvas) {
        throw new Error('PDF canvas capture failed');
      }
      pdf.addPage([wMm, hMm], orientation);
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.98), 'JPEG', 0, 0, wMm, hMm, undefined, 'FAST');
    }

    const blob = pdf.output('blob');
    if (!blob || blob.size < 100) {
      throw new Error('Generated PDF was empty');
    }
    return blob;
  };

  try {
    return await run(false);
  } catch (err) {
    if (isCanvasTaintError(err)) {
      try {
        return await run(true);
      } catch (retryErr) {
        throw new Error(retryErr?.message || String(retryErr) || 'PDF generation failed');
      }
    }
    throw new Error(err?.message || String(err) || 'PDF generation failed');
  }
}

export async function buildIdCardHtmlPair(student, school, photoUrl, opts = {}) {
  const format = opts.format || 'portrait';
  const design = opts.design || 'saffron';
  const imageCacheKey = opts.imageCacheKey || '';
  const size = CARD_FORMATS[format] || CARD_FORMATS.portrait;
  const portrait = format !== 'landscape';
  const base = buildIdCardData(student, school, photoUrl, imageCacheKey);
  const qrDataUrl = await qrDataUrlFor(base.qrPayload);
  const data = await inlineImageUrls({ ...base, qrDataUrl });
  return {
    frontHtml: buildFrontHtml(data, size.widthPx, size.heightPx, portrait, design),
    backHtml: buildBackHtml(data, size.widthPx, size.heightPx, portrait, design),
    size,
    data,
    design,
    format,
  };
}

export async function buildIdCardPairs(students, school, opts = {}, onProgress) {
  const format = opts.format || 'portrait';
  const design = opts.design || 'saffron';
  const imageCacheKey = opts.imageCacheKey || '';
  const pairs = [];
  const total = students.length;
  for (let i = 0; i < total; i++) {
    const student = students[i];
    const photoUrl = getProfileAssetUrl(student.avatar);
    const pair = await buildIdCardHtmlPair(student, school, photoUrl, { format, design, imageCacheKey });
    pairs.push(pair);
    onProgress?.({ done: i + 1, total, label: `Building card ${i + 1} of ${total}…` });
  }
  return pairs;
}

export { buildPrintSheetHtml, computeSheetGrid } from './idCardSheetLayout';

/** @deprecated Use buildPrintSheetHtml */
export { buildSheetPreviewHtml } from './idCardSheetLayout';

export async function renderIdCardPdfBlob(student, school, photoUrl, { format = 'portrait', design = 'saffron', imageCacheKey = '' } = {}) {
  const { frontHtml, backHtml, size } = await buildIdCardHtmlPair(student, school, photoUrl, { format, design, imageCacheKey });
  const wMm = size.widthIn * 25.4;
  const hMm = size.heightIn * 25.4;
  const documentHtml = buildSingleCardDocumentHtml({ frontHtml, backHtml, size });
  const pageWidthPx = Math.ceil(mmToCssPx(wMm));
  const pageHeightPx = Math.ceil(mmToCssPx(hMm));

  const html2pdf = await getHtml2pdf();
  const { iframe, doc } = buildCaptureDocument(documentHtml, pageWidthPx, pageHeightPx * 2);

  try {
    await waitForImages(doc.body);
    fitIframeToContent(iframe, doc, pageWidthPx, pageHeightPx * 2);
    return await capturePageElementsToPdfBlob(html2pdf, doc, {
      wMm,
      hMm,
      filename: 'temp.pdf',
      captureWidthPx: pageWidthPx,
      captureHeightPx: pageHeightPx,
      scale: 2,
    });
  } finally {
    iframe.remove();
  }
}

export async function renderPrintSheetPdfBlob(pairs, printSettings = {}) {
  const settings = mergePrintSettings(printSettings);
  const sheetHtml = buildPrintSheetHtml(pairs, settings);
  if (!sheetHtml) throw new Error('No cards to render');

  const grid = computeSheetGrid(settings);
  const pageWidthPx = Math.ceil(mmToCssPx(grid.pageWidthMm));
  const pageHeightPx = Math.ceil(mmToCssPx(grid.pageHeightMm));

  const html2pdf = await getHtml2pdf();
  const { iframe, doc } = buildCaptureDocument(sheetHtml, pageWidthPx, pageHeightPx);

  try {
    await waitForImages(doc.body);
    fitIframeToContent(iframe, doc, pageWidthPx, pageHeightPx);
    return await captureHtmlToPdfBlob(html2pdf, doc, {
      wMm: grid.pageWidthMm,
      hMm: grid.pageHeightMm,
      filename: 'temp.pdf',
      captureWidthPx: pageWidthPx,
      captureHeightPx: pageHeightPx,
      scale: 2,
    });
  } finally {
    iframe.remove();
  }
}

export async function downloadIdCardPdf(student, school, photoUrl, { format = 'portrait', design = 'saffron', imageCacheKey = '' } = {}) {
  const fn = `id-${student.admission_no || student.id}-${format}.pdf`;
  const blob = await renderIdCardPdfBlob(student, school, photoUrl, { format, design, imageCacheKey });
  downloadBlob(blob, fn);
  return fn;
}

export async function downloadPrintSheetPdf(pairs, printSettings = {}, filename = null) {
  const blob = await renderPrintSheetPdfBlob(pairs, printSettings);
  const fn = filename || `id-cards-sheet-${Date.now()}.pdf`;
  downloadBlob(blob, fn);
  return fn;
}

export async function printIdCard(student, school, photoUrl, { format = 'portrait', design = 'saffron', imageCacheKey = '' } = {}) {
  const { frontHtml, backHtml, size } = await buildIdCardHtmlPair(student, school, photoUrl, { format, design, imageCacheKey });
  const documentHtml = buildSingleCardDocumentHtml({ frontHtml, backHtml, size });
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.write(documentHtml);
  win.document.close();
  await waitForImages(win.document.body);
  setTimeout(() => win.print(), 600);
  return true;
}

export async function printPrintSheet(pairs, printSettings = {}) {
  const sheetHtml = buildPrintSheetHtml(pairs, mergePrintSettings(printSettings));
  if (!sheetHtml) return false;
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.write(sheetHtml);
  win.document.close();
  await waitForImages(win.document.body);
  setTimeout(() => win.print(), 800);
  return true;
}

export async function bulkDownloadIdCards({
  students,
  school,
  format = 'portrait',
  design = 'saffron',
  imageCacheKey = '',
  output = 'merged',
  printSettings = {},
  onProgress,
}) {
  if (!students?.length) throw new Error('No students selected');

  const settings = mergePrintSettings({ ...printSettings, cardFormat: format });
  const total = students.length;
  const cardOpts = { format, design, imageCacheKey };

  if (output === 'merged') {
    onProgress?.({ done: 0, total, label: 'Building cards…' });
    const pairs = await buildIdCardPairs(students, school, cardOpts, onProgress);
    onProgress?.({ done: total, total, label: 'Rendering print sheet…' });
    const blob = await renderPrintSheetPdfBlob(pairs, settings);
    const filename = `id-cards-sheet-${Date.now()}.pdf`;
    downloadBlob(blob, filename);
    return { filename, mime: 'application/pdf' };
  }

  const pdfBlobs = [];
  const zipEntries = [];

  for (let i = 0; i < students.length; i++) {
    const student = students[i];
    const photoUrl = getProfileAssetUrl(student.avatar);
    const blob = await renderIdCardPdfBlob(student, school, photoUrl, { format, design, imageCacheKey });
    const name = `id-${student.admission_no || student.id}-${format}.pdf`;
    pdfBlobs.push(blob);
    zipEntries.push({ name, blob });
    onProgress?.({ done: i + 1, total, label: `Generating ${i + 1} of ${total}…` });
  }

  onProgress?.({ done: total, total, label: 'Assembling ZIP…' });
  const zip = new JSZip();
  zipEntries.forEach(({ name, blob }) => zip.file(name, blob));
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const filename = `id-cards-${Date.now()}.zip`;
  downloadBlob(zipBlob, filename);
  return { filename, mime: 'application/zip' };
}

export { mergePrintSettings, cardSizeFromSettings, DEFAULT_PRINT_SETTINGS } from './idCardPrintSettings';
